import { differenceInDays } from 'date-fns';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import type { BillingInfoType } from '@sdk/account/api/AccountApi';
import getNowDate from '@sdk/utils/getNowDate';
import { getFirstDayAfterCardExpiration } from '@sdk/account/utils';

import { WarningType } from '@web/account/billingWarnings/ExpiryWarningModal/expiryWarningModal.types';

const EXPIRING_SOON_DAYS = 7;

export const shouldShowAccountLockedModal = (
  billingInfo: BillingInfoType,
  currentUser: AuthUserType
) => {
  return (
    !currentUser.settings.clientFlags[
      `subscriptionExpiryWarning-${WarningType.accountLocked}-${billingInfo.creditCard.lastFour}-${billingInfo.paymentFailedDate}-closed`
    ] &&
    billingInfo.paymentFailedDate &&
    !billingInfo.isPremium
  );
};

export const shouldShowPaymentFailedModal = (
  billingInfo: BillingInfoType,
  currentUser: AuthUserType
) => {
  return (
    !currentUser.settings.clientFlags[
      `subscriptionExpiryWarning-${WarningType.paymentFailed}-${billingInfo.creditCard.lastFour}-${billingInfo.paymentFailedDate}-closed`
    ] &&
    billingInfo.paymentFailedDate &&
    billingInfo.isPremium
  );
};

export const shouldShowCardAuthenticationFailedModal = (
  billingInfo: BillingInfoType,
  currentUser: AuthUserType
) => {
  return (
    !currentUser.settings.clientFlags[
      `subscriptionExpiryWarning-${WarningType.cardAuthenticationFailed}-${billingInfo.creditCard.lastFour}-${billingInfo.paymentFailedDate}-closed`
    ] &&
    billingInfo.paymentFailedDate &&
    billingInfo.paymentFailedClientSecret
  );
};

export const shouldShowCardExpiredModal = (
  billingInfo: BillingInfoType,
  currentUser: AuthUserType
) => {
  return (
    !currentUser.settings.clientFlags[
      `subscriptionExpiryWarning-${WarningType.cardExpired}-${billingInfo.creditCard.lastFour}-${billingInfo.paymentFailedDate}-closed`
    ] &&
    billingInfo.creditCard?.type &&
    (billingInfo.subscriptionStartDate || billingInfo.paymentFailedDate) &&
    differenceInDays(getFirstDayAfterCardExpiration(billingInfo.creditCard), getNowDate()) <= 0
  );
};

export const shouldShowCardExpiringSoonModal = (
  billingInfo: BillingInfoType,
  currentUser: AuthUserType
) => {
  const daysToExpire = differenceInDays(
    getFirstDayAfterCardExpiration(billingInfo.creditCard),
    getNowDate()
  );
  return (
    !currentUser.settings.clientFlags[
      `subscriptionExpiryWarning-${WarningType.cardExpiringSoon}-${billingInfo.creditCard.lastFour}-${billingInfo.paymentFailedDate}-closed`
    ] &&
    billingInfo.creditCard &&
    billingInfo.subscriptionStartDate &&
    daysToExpire > 0 &&
    daysToExpire <= EXPIRING_SOON_DAYS
  );
};
